// @flow
import * as React from "react";

import type Api from "../api/index.js";
import type Options from "./options.js";
import type UrlResolver, {Hierarchy} from "./urlresolver.js";
import type {Content, Publishable, Positions} from "../api/types.js";
import {shouldLoadChildren, isMultipleItemStyle} from "../api/items.js";

import getFonts from "../fonts.js";

import Kosk from "../screens/kosk.js";
import Search from "../screens/search.js";
import TagScreen from "../screens/tag.js";
import TopLiked from "../screens/top-liked.js";
import Collection from "../screens/collection.js";
import OldCollection from "../screens/oldcollection.js";
import Reader from "../screens/reader.js";
import Forward from "../screens/forward.js";
import PasswordScreen from "../screens/password.js";
import loadPagehandler from "../pagehandler/load.js";
import EmbedWidget from "../screens/embedwidget.js";
import {hasLocalStorage} from "../environment.js";
import {getBestMode} from "../components/modeswitcher.js";
import {hasSectionInSection} from "../publishables/flow.js";

type Resolve = {
	back?: string;
	content: ?Content;
	// So flow now has problems with this?
	// https://github.com/facebook/flow/releases/tag/v0.39.0
	element: any;
};

/**
 * Helper class for resolving data from the pagestrip API
 */
class DataResolver {
	options: Options;
	api: Api;
	url: UrlResolver;

	constructor(options: Options, api: Api, url: UrlResolver) {
		this.options = options;
		this.api = api;
		this.url = url;
	}

	/**
	 * Resolves the data specified by the URLResolver given in the constructor
	 * @returns {Promise<Resolve>}
	 */
	async resolve(): Promise<Resolve> {
		const {options, api, url} = this;
		const {yeOldeKosk, context, viewport, isEmbedded, stylePrefix, appearanceId} = options;
		const {hierarchy, query, search, anchor, topliked, tagged} = url;
		const result = {};
		result.content = null;

		const tags = query.tags && query.tags.split(',');

		const publisherQueryArgs = appearanceId ? {appearanceId} : null;

		// XXX: just always load the context for now, since in most cases we do
		// need it for the back button and prev/next, and loading it delayed via
		// that mechanism is actually a lot slower -_-
		if (context) {
			result.publisher = await api.resolve(context, undefined, tags, publisherQueryArgs);
			if (!isEmbedded && String(context) !== String(parseInt(context, 10))) { //the context isn't a publisher
				try {
					result.publisher = await api.resolve(result.publisher.publisher.id, undefined, undefined, publisherQueryArgs);
				} catch (e) {
					console.error(e);
					result.publisher = null;
				}
			}
		}

		if (search) {
			if (!result.publisher) {
				throw new Error("search not possible without publisher context");
			}
			const results = await api.search(result.publisher.id, search, query.page);
			result.back = this.url.create({query: {q: undefined}});
			result.element = (<Search results={results} term={search} />);
			return result;
		} else if (topliked) {
			if (!result.publisher) {
				throw new Error("top likes not possible without publisher context");
			}
			const results = await api.topLiked(result.publisher.id, query.page);
			result.back = this.url.create({query: {topliked: undefined}});
			result.element = (<TopLiked results={results} />);
			return result;
		} else if (tagged) {
			if (!result.publisher) {
				throw new Error("tag not possible without publisher context");
			}
			const results = await api.tagged(result.publisher.id, tagged, query.page);
			result.back = this.url.create({query: {t: undefined}});
			result.element = (<TagScreen content={{ publisher: result.publisher, results}} term={tagged.join(', ')} />);
			return result;
		}

		// resolve the content itself
		const topmost = hierarchy[hierarchy.length - 1] || isEmbedded && result.publisher && result.publisher.index && result.publisher.id || !isEmbedded && context;
		if (!topmost) {
			throw new Error("no item to resolve");
		}

		const topIsPublisher = String(topmost) === String(parseInt(topmost, 10));
		let content = await api.resolve(topmost, topIsPublisher ? parseInt(query.page || 1) : undefined, tags, topIsPublisher ? publisherQueryArgs : undefined);
		if (!content) {
			throw new Error("resolving not possible");
		}
		result.content = content;
		if (content.type === 'story' && content.translations && content.translations[url.locale] && content.translations[url.locale].id !== content.id) {
			content = await api.resolve(content.translations[url.locale].id, undefined, tags);
			result.content = content || result.content;
			content = result.content;
		}

		if (!result.publisher && content.publisher) {
			result.publisher = content.publisher;
		} else if (result.publisher.id !== content.publisher.id) {
			const err = new Error("content not found in this publisher");
			err.content = result.publisher; 
			throw err;
		}

		//in case the locale changed during the content resolve
		url.locale = options.locale;

		if (content.type === "user" || content.type === "publisher") {
			if (content.index) {
				//index publishable
				content = content.index;
				if (content.translations && content.translations[url.locale] && content.translations[url.locale].id !== content.id) {
					content = await api.resolve(content.translations[url.locale].id, undefined, tags) || content;
				}
			} else {

				if (content.appendPaging && content.currentPage > 1) {
					const p = content.currentPage;
					//load previous pages if in append mode
					for (let c = 1; c < p; c++) {
						if (!content.loaded[c]) await api.resolve(content.id, c, tags, publisherQueryArgs);
					}
					content.setPage(p);
				}

				// Kosk
				if (!yeOldeKosk) {
					await this.resolveChildContents({positions: content.positions});
				}
				if (isEmbedded) {
					result.back = await this.resolveBack();
				}
				result.element = (<Kosk kosk={content} page={content.currentPage || 1} back={isEmbedded} />);
				return result;
			}
		}

		// $FlowFixMe: flow is unable to assert the type here! :-(
		const publishable: Publishable = content;

		if ("embedwidget" in query) {
			result.element = (<EmbedWidget content={publishable} />);
			return result;
		}

		result.back = await this.resolveBack();

		if (publishable.loginRequired) {
			result.element = (<PasswordScreen content={publishable} />);
			return result;
		}

		if (publishable.type === "collection") {
			// Collection
			if (!yeOldeKosk) {
				const {options: { layout } = {}} = publishable;
				await this.resolveChildContents({positions: publishable.positions}, hasSectionInSection(result.publisher) || layout && layout.split(',').map(s => s.trim()).some(isMultipleItemStyle));
			}
			const Component = yeOldeKosk ? OldCollection : Collection;
			result.element = (<Component content={publishable} collection={publishable} isEmbedded={options.isEmbedded} host={options.absoluteHost} />);
			return result;
		}

		if (publishable.type === "link") {
			result.element = <Forward href={publishable.href} />
			return result;
		}

		// Story
		const {noFlow} = publishable.options || {};
		let {page, mode} = query;

		if (!page || IS_BROWSER) {
			let npage = page;
			const {width, height} = viewport;
			let bestMode = noFlow && 'desktop' || mode || getBestMode(width, height);
			if (bestMode === 'mobile') {
				npage = 'flow';
			} else if (bestMode === 'desktop') {
				npage = page === 'flow' ? '1' : page || '1';
			} else {
				npage = page || '1';
			}
			if (npage === '1' && publishable.pages.length === 1) {
				npage = undefined;
			}
			if(npage !== page) {
				page = npage;
				url.redirect({query: {page, tags: query.tags}, anchor});
			}
		}

		let pagePromise;
		if (page === "flow") {
			pagePromise = publishable.flow.load(stylePrefix);
		} else {
			const num = (parseInt(page, 10) || 1) - 1;
			if (num >= publishable.pages.length) {
				throw new Error("Sorry, but the requested page does not exist.");
			}
			pagePromise = publishable.pages[num].load(stylePrefix);
		}

		let prevNext = options.prevNext;
		let prevNextMode = options.prevNextMode;
		if (result.publisher && result.publisher.appearance && Array.isArray(result.publisher.appearance.styles)) {
			const { styles } = result.publisher.appearance;
			const { def } = styles.find(s => s.ns === 'Settings' && s.name === 'ShowPrevNext') || {};
			prevNext = def === true || def !== false && prevNext;
			const { def: mode } = styles.find(s => s.ns === 'Settings' && s.name === 'PrevNextMode') || {};
			prevNextMode = mode || prevNextMode;
		}

		//resolve prev next
		if (prevNext) {
			try {
				if(prevNextMode === 'api') {
					prevNext = publishable.context || await api.resolveContext(publishable.id, hierarchy.slice(0, -1), tags);
					
					const prepend = [];
					if(hierarchy.length > 1) {
						//check if the first item in the hierarchy is a story
						const p = await api.resolve(hierarchy[0]);
						if (p && p.type === "story") {
							prepend.push(p.id);
						}
					}

					const flatten = h => ({
						href: this.url.create({hierarchy: [...prepend, ...h.map(p => p.lookupId)], slug: h[h.length - 1].slug, query: query.tags ? {tags: query.tags} : null }),
						parent: h.length > 1 ? this.api.register(h[h.length - 2]) : null,
						story: this.api.register(h[h.length - 1]),
					})
					prevNext.next = prevNext.next && !!prevNext.next.length && flatten(prevNext.next);
					prevNext.prev = prevNext.prev && !!prevNext.prev.length && flatten(prevNext.prev);
				} else {
					prevNext = {
						next: await this.resolvePrevNext(1),
						prev: await this.resolvePrevNext(-1),
					}
				}
			} catch (err) {
				// fail silently. we're not going to crash the entire page, if prevnext yields 404
			}
		} else {
			prevNext = {};
		}

		const {prev, next} = prevNext;

		const [fonts] = await Promise.all([
			getFonts(publishable.fonts).promise,
			pagePromise,
			loadPagehandler,
		]);
		result.element = (<Reader story={publishable} page={page} fonts={fonts} prev={prev || null} next={next || null} anchor={url.anchor} zoom={url.query.zoom} stylePrefix={stylePrefix} />);
		return result;
	}

	/**
	 * This resolves the content of child items that display their content
	 * inline. For example collections displayed as `section`.
	 * @param {Object} descriptor - An Object containing the field Positions
	 * @param {Positions} descriptor.positions - The Positions to fetch the content for
	 * @return {Promise}
	 */
	async resolveChildContents({positions}: {positions?: Positions}, ifNoStyle: boolean = true): Promise<*> {
		if (!positions) { return; }
		const childrenToLoad = [];

		// TODO: walk all positions
		{
			const section = positions.main;
			for (let i = 0; i < section.length; i++) {
				const item = section[i];
				if (item.type !== "publishable") { continue; }
				if (!shouldLoadChildren(item, ifNoStyle)) { continue; }
				childrenToLoad.push(item.content.load());
			}
		}

		return Promise.all(childrenToLoad).then(result => {
			return Promise.all(result.map(item => {
				return this.resolveChildContents(item, false);
			}));
		});
	}

	/**
	 * Helper function that tries to add the context to the hierarchy and returns the result
	 * @return {Object} -  containing the fields `withContext` and `hierarchy`
	 */
	hierarchyWithContext() {
		const {context, isEmbedded} = this.options;
		const hierarchy = this.url.hierarchy.slice();

		let withContext = !isEmbedded && Boolean(context);
		if (withContext && hierarchy[0] === context) {
			withContext = false;
		}
		if (withContext) {
			// flow can’t assert that context is non-null :-(
			hierarchy.unshift((context: any));
		}
		return {
			withContext,
			hierarchy,
		};
	}

	/**
	 * Creates a back link from the url given in the constructor
	 * @return {Promise<string>}
	 */
	async resolveBack(): Promise<string> {
		const {url, options: {yeOldeKosk, isEmbedded}} = this;
		const {hierarchy, withContext} = this.hierarchyWithContext();
		const content = await this.api.resolve(hierarchy[hierarchy.length - 1]);
		
		if (content && content.options && content.options.skipToKosk) {
			return url.create({hierarchy: [], context: content.publisher.id});
		}

		hierarchy.pop();

		if (hierarchy.length) {
			// we have to resolve the parent anyway, to have its slug
			const parent = await this.api.resolve(hierarchy[hierarchy.length - 1]);

			if (parent && parent.options) {
				if (parent.options.skipToContext) {
					//if the option skipToContext is enabled just remove all element but the first one
					//from the hierarchy.
					hierarchy.splice(1, hierarchy.length);	
				} else if (parent.options.skipToKosk) {
					//if the option skipToKosk is enabled just remove all elements from the hierarchy.
					//and go straight to the kosk
					return url.create({hierarchy: [], context: parent.publisher.id});
				}
			}

			// if we have a new kosk, look at the parents parent, and see if we can
			// skip the parent
			if (!yeOldeKosk && hierarchy.length > 1) {
				let grandParent = await this.api.resolve(hierarchy[hierarchy.length - 2]);
				let currentParent = parent;
				while (currentParent && currentParent.type !== "story" && currentParent.type !== "link") {
					if (currentParent && (currentParent.options && currentParent.options.skipToContext)) {
						//just jump straight to the top of the hierarchy and remove the rest
						hierarchy.splice(1, hierarchy.length);
					} else if (currentParent && (currentParent.options && currentParent.options.skipToKosk)) {
						//just jump straight to the kosk
						return url.create({hierarchy: [], context: currentParent.publisher.id});
					} else if (
						(grandParent.type === 'user' || grandParent.type === 'publisher')
							&& shouldLoadChildren(currentParent)
							&& grandParent.positions && grandParent.positions.main && !!grandParent.positions.main.find(item => item && shouldLoadChildren(item) && item.content === currentParent)
						||
						grandParent.type === 'collection'
							&& shouldLoadChildren(currentParent, false)
					) {
						//if the grandparent is a collection check if it would load and display the
						//content if rendered in its parent. If the grandParent is a user / publisher
						//check if the currentParent us contained in the main position's list.
						//If yes remove the current parent from the hierarchy.
						hierarchy.pop();
					} else { break; }

					if (hierarchy.length > 1) {
						currentParent = grandParent;
						grandParent = await this.api.resolve(hierarchy[hierarchy.length - 2]);
					} else { break; }
				}
			}
		}
		if (!hierarchy.length) {
			return isEmbedded ? "/" : "";
		}
		if (withContext) {
			hierarchy.shift();
		}
		if (!hierarchy.length && url.query.q) {
			return url.create({search: url.query.q});
		}
		if (!hierarchy.length && url.query.topliked !== undefined) {
			return url.create({topliked: true});
		}
		if (!hierarchy.length && url.query.t) {
			return url.create({tagged: url.query.t.split(',')});
		}

		if (!hierarchy.length && !isEmbedded) { //we will go back to the context / kosk
			const context = this.api.resolveSync(this.options.context);
			if (context.currentPage > 1) {
				return url.create({hierarchy, query: {page: context.currentPage}});
			}
		}

		return url.create({hierarchy, query: {tags: url.query.tags}});
	}

	/**
	 * Resolves the previous or next story from the current internal context
	 * @param {-1|1} direction - The direction to resolve in: -1 = previous, +1 = next
	 * @return {Promise<Object>} - The promise returns either null or an Object containing
	 *                             the href and story descriptor Object
	 */
	async resolvePrevNext(direction: -1 | 1): Promise<*> {
		const {hierarchy, withContext} = this.hierarchyWithContext();
		const resolved = await this.resolveSibling(hierarchy, direction);
		if (!resolved) { return null; }
		if (withContext) {
			resolved.shift();
		}
		return {
			href: this.url.create({hierarchy: resolved}),
			story: this.api.resolveSync(resolved[resolved.length - 1]),
		};
	}

	/**
	 * Resolves the sibling in the given direction in the given hierarchy
	 * @param {-1|1} direction - The direction to resolve in: -1 = previous, +1 = next
	 * @returns {Promise<Hierarchy>}
	 */
	async resolveSibling(hierarchy: Hierarchy, direction: -1 | 1): Promise<?Hierarchy> {
		if (hierarchy.length < 2) { return null; }
		let parent;
		const parents = hierarchy.slice(0, hierarchy.length - 1);
		while(!parent) {
			parent = parents.pop();
			if (!parent) { return null; }
			parent = await this.api.resolve(parent);
			if (!parent) { return null; }
			if (parent.type === "story" || parent.type === "link") { parent = null }
		}
		const siblings = parent.content;

		const self = hierarchy[hierarchy.length - 1];
		if (!siblings) { return null; }
		let index = siblings.findIndex(sibling => sibling && sibling.id === self);
		if (index === -1) {
			while (parent.currentPage < parent.pageCount) { //maybe the item is on another page
				await this.api.resolve(parent.id, parent.currentPage + 1);
				index = siblings.findIndex(sibling => sibling && sibling.id === self);
			}
			if (index === -1) {
				return null;
			}
		}
		let toIndex = index + direction;
		const base = hierarchy.slice(0, hierarchy.length - 1);
		while (toIndex >= 0 && toIndex < siblings.length) {
			// we have a direct sibling, so use that
			let sibling = siblings[toIndex];
			if (!sibling) { //the sibling is probably on another page. Fetch that and retry.
					await this.api.resolve(parent.id, parent.currentPage + direction);
					sibling = siblings[toIndex];
			}
			if (sibling.type === 'link') {
				toIndex += direction;
				continue;
			}

			//XXX: this is a hack so that sliders at the top of the kosk don't show up as previous story
			if (toIndex === 0 && sibling.options.style === 'slider') {
				break;
			}
			return this.resolveFirstOrLast([...base, sibling.id], direction);
		}
		// we need to recurse to the parent
		return this.resolveSibling(base, direction);
	}

	/**
	 * Checks if the top element in the given hierarchy is a collection or story. If it is a
	 * story it is returned. Otherwise the function tries to find the first or last story in
	 * the collection based on the given direction.
	 * @param {-1|1} direction - The direction to resolve in: -1 = last, +1 = first
	 * @returns {Promise<Hierarchy>}
	 */
	async resolveFirstOrLast(hierarchy: Hierarchy, direction: -1 | 1): Promise<?Hierarchy> {
		const self = this.api.resolveSync(hierarchy[hierarchy.length - 1]);
		if (!self) { return null; }
		if (self.type === "story" || self.type === "link") {
			return hierarchy;
		}

		if (!self.loaded) {
			await self.load();
		}
		const content = self.content;
		if (!content || !content.length) {
			// go to the next sibling if the collection is empty
			return this.resolveSibling(hierarchy, direction);
		}
		const index = direction === -1 ? content.length - 1 : 0;
		const item = content[index];
		if (hierarchy.includes(item.id)) {
			console.error(`Collection "${item.id}" creates a cycle!`);
			return null;
		}
		return this.resolveFirstOrLast([...hierarchy, item.id], direction);
	}
}

export default DataResolver;



// WEBPACK FOOTER //
// ./lib/player/dataresolver.js